// src/components/ui/modal/ArticleModal.tsx

'use client';

import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import styles from './ArticleModal.module.scss';
import HeaderContent from '@/components/header/article/HeaderContent';

type ModalProps = {
  /** 是否開啟 */
  open: boolean;
  /** 關閉回呼（點×、按 ESC、點遮罩時會觸發） */
  onClose: () => void;
  /** 標題（做為 aria-labelledby 來源） */
  title?: ReactNode;
  /** 內容 */
  children: ReactNode;
  /** 寬度（例如 480、'min(92vw, 560px)'） */
  width?: number | string;
  /** 點擊遮罩是否關閉（預設 true） */
  closeOnBackdrop?: boolean;
  /** 指定打開後的初始聚焦元素 */
  initialFocusRef?: RefObject<HTMLElement>;
  /** 若無 title，可用 ariaLabel 描述對話框用途 */
  ariaLabel?: string;
};

export default function Modal({
  open,
  onClose,
  title,
  children,
  width = 'min(92vw, 560px)',
  closeOnBackdrop = true,
  initialFocusRef,
  ariaLabel,
}: ModalProps) {
  const [mounted, setMounted] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const lastFocused = useRef<HTMLElement | null>(null);

  // 僅在 client 端建立 portal
  useEffect(() => setMounted(true), []);

  // 開啟時：鎖 body 捲動、初始聚焦、鍵盤事件、焦點陷阱；關閉時：解除鎖定並把焦點還原
  useEffect(() => {
    if (!open) return;

    lastFocused.current = document.activeElement as HTMLElement;

    const focusFirst = () => {
      const toFocus =
        initialFocusRef?.current ??
        dialogRef.current?.querySelector<HTMLElement>('[data-autofocus]') ??
        dialogRef.current?.querySelector<HTMLElement>(
          'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])'
        );
      toFocus?.focus?.();
    };

    focusFirst();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        // 簡易 focus trap
        const nodes = dialogRef.current?.querySelectorAll<HTMLElement>(
          'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])'
        );
        if (!nodes || nodes.length === 0) return;
        const list = Array.from(nodes);
        const first = list[0];
        const last = list[list.length - 1];
        const active = document.activeElement as HTMLElement | null;

        if (e.shiftKey) {
          if (active === first || !list.includes(active!)) {
            last.focus();
            e.preventDefault();
          }
        } else {
          if (active === last || !list.includes(active!)) {
            first.focus();
            e.preventDefault();
          }
        }
      }
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      // 關閉後回到開啟前的焦點（純本頁用法時生效）
      try {
        lastFocused.current?.focus?.({ preventScroll: true });
      } catch {
        lastFocused.current?.focus?.();
      }
    };
  }, [open, onClose, initialFocusRef]);

  if (!mounted || !open) return null;

  // ─────────────────────────────
  // Header：有傳 title 就用外面的；沒傳就用預設 HeaderContent
  // ─────────────────────────────
  const headerNode =
    title ??
    // 預設版本：沒有文章資料，只顯示返回箭頭＋灰頭像（給其它地方沿用）
    ((
      <HeaderContent
        backBtnShow="mobile-only"
        backBtnClassName="me-1"
        sourceLabel="modal-default"
      />
    ) as ReactNode);

  const headerId = headerNode ? 'modal-title' : undefined;

  return createPortal(
    <div
      className={styles.backdrop}
      role="presentation"
      onMouseDown={(e) => {
        if (!closeOnBackdrop) return;
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <button type="button" className={styles.closeBtn} aria-label="關閉" onClick={onClose}>
        ×
      </button>
      <div
        ref={dialogRef}
        className={styles.modal}
        style={{ width }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headerId}
        aria-label={headerId ? undefined : ariaLabel}
      >
        {headerNode && (
          <div className={styles.header} id={headerId}>
            {headerNode}
          </div>
        )}

        <div className={styles.body}>{children}</div>
      </div>
    </div>,
    document.body
  );
}
